import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectOptions } from 'store/options';
import EmptyState from 'components/EmptyState';
import useCreateNewGoal from 'hooks/GoalsAndSubgoalsForm/useCreateNewGoal';
import { GoalField } from 'components/GoalsAndSubgoalsForm/interfaces';
import TabStrip, {
  ALL_GOALS,
  ActiveTab,
} from 'pages/Scorecard/ViewScorecard/TabStrip.component';
import UndoToastsProvider, {
  useUndoToasts,
} from 'pages/Scorecard/EditScorecard/UndoToasts.component';
import { SectionsCard } from 'pages/Scorecard/EditScorecard/EditScorecard.styled';
import CommonGoalEditor from './CommonGoalEditor.component';

interface CommonGoalsEditFormProps {
  canAddGoals: boolean;
}

function CommonGoalsEditFormContent({ canAddGoals }: CommonGoalsEditFormProps) {
  const { control, watch, setValue, getValues, trigger } =
    useFormContext<GoalField>();
  const { paramTypes } = useSelector(selectOptions);
  const createNewGoal = useCreateNewGoal(paramTypes);
  const [activeTab, setActiveTab] = useState<ActiveTab>(ALL_GOALS);
  const { fields, append, move, remove, insert } = useFieldArray<
    GoalField,
    'goals',
    'key'
  >({ control, name: 'goals', keyName: 'key' });
  const { pushUndoToast } = useUndoToasts();
  const watchedGoals = watch('goals');
  const goals = useMemo(() => watchedGoals ?? [], [watchedGoals]);

  // Newly added (unsaved) goals have no backend `id`. Synthesise the same
  // negative key in both the tab strip and the visibility filter so clicking
  // the tab of a newly-added goal actually shows it.
  const goalTabKey = (goal: GoalField['goals'][number] | undefined, idx: number) =>
    (goal?.id ?? -1 - idx) as number;
  const visibleIndices =
    activeTab === ALL_GOALS
      ? fields.map((_, idx) => idx)
      : fields.reduce<number[]>((acc, _, idx) => {
          if (goalTabKey(goals[idx], idx) === activeTab) acc.push(idx);
          return acc;
        }, []);

  useEffect(() => {
    if (typeof activeTab !== 'number' || activeTab >= 0) return;
    const idx = -1 - activeTab;
    const realId = goals[idx]?.id;
    if (typeof realId === 'number' && realId >= 0) {
      setActiveTab(realId);
    }
  }, [goals, activeTab]);

  const handleAddGoal = () => {
    if (!canAddGoals) return;
    const newIdx = fields.length;
    const created = createNewGoal(newIdx, true);
    append({
      ...created,
      subgoals: created.subgoals.map((subgoal, idx) =>
        idx === 0 ? { ...subgoal, weight: 100 } : subgoal,
      ),
    } as never);
    setValue('goals', getValues('goals'), { shouldDirty: true });
    setActiveTab(goalTabKey(undefined, newIdx));
    setTimeout(() => {
      trigger(`goals.${newIdx}`);
    }, 0);
  };

  const handleRemoveGoal = (idx: number) => {
    const removed = getValues('goals')[idx];
    if (!removed) return;
    const removedTabKey = goalTabKey(removed, idx);
    remove(idx);
    setValue('goals', getValues('goals'), { shouldDirty: true });
    if (activeTab === removedTabKey) setActiveTab(ALL_GOALS);
    pushUndoToast({
      message: (
        <FormattedMessage
          id="pages.scorecards.edit.goalDeleted"
          defaultMessage='Goal "{name}" deleted'
          values={{ name: removed.name || `Goal ${idx + 1}` }}
        />
      ),
      onUndo: () => {
        insert(idx, removed as never);
        setValue('goals', getValues('goals'), { shouldDirty: true });
        setTimeout(() => {
          trigger(`goals.${idx}`);
        }, 0);
      },
    });
  };

  const handleReorderGoals = (fromIdx: number, toIdx: number) => {
    if (!canAddGoals || fromIdx === toIdx) return;
    move(fromIdx, toIdx);
    setValue('goals', getValues('goals'), { shouldDirty: true });
  };

  const tabsForStrip = goals.map((g, idx) => ({
    id: goalTabKey(g, idx),
    name: g.name || `Goal ${idx + 1}`,
    weight: g.weight,
  })) as never;

  return (
    <>
      <TabStrip
        goals={tabsForStrip}
        active={activeTab}
        onChange={setActiveTab}
        onReorder={canAddGoals ? handleReorderGoals : undefined}
        showWeight={false}
        actions={
          canAddGoals ? (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<AddIcon />}
              onClick={handleAddGoal}
            >
              <FormattedMessage
                id="pages.reviewCycles.addGoal"
                defaultMessage="Add Goal"
              />
            </Button>
          ) : undefined
        }
      />
      {fields.length === 0 ? (
        <EmptyState
          title={
            <FormattedMessage
              id="pages.reviewCycles.noGoals"
              defaultMessage="There are no Goals in this Category."
            />
          }
        />
      ) : (
        <SectionsCard
          sx={{
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {visibleIndices.map((idx) => {
            const goalId = goals[idx]?.id;
            return (
              <div
                key={fields[idx].key}
                id={goalId ? `goal-${goalId}` : undefined}
                style={{ scrollMarginTop: 60 }}
              >
                <CommonGoalEditor
                  goalIndex={idx}
                  goalsCount={fields.length}
                  onRemove={() => handleRemoveGoal(idx)}
                />
              </div>
            );
          })}
        </SectionsCard>
      )}
    </>
  );
}

export default function CommonGoalsEditForm(props: CommonGoalsEditFormProps) {
  return (
    <UndoToastsProvider>
      <CommonGoalsEditFormContent {...props} />
    </UndoToastsProvider>
  );
}
